import { Grid, IconButton, Tab, Tabs, Tooltip, useTheme } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TabContext, TabPanel } from '@mui/lab';
import Typography from '@mui/material/Typography';
import styled from 'styled-components';
import { Edit, Email, Redeem } from '@mui/icons-material';
import ConfirmationNumber from '@mui/icons-material/ConfirmationNumber';
import EventParticipantsTable from './EventParticipantsTable';
import EventPassHoldsTable from './EventPassHoldsTable';
import {
  AccountData,
  EnrichedEventData,
  MessageRecipientType,
  OrderData,
  WaitingListItemData,
  WaitingListService,
} from '../../../../api';
import { CellProps, useTable } from '../../../../table/useTable';
import { buildWaitingListExportColumns } from './waitingListExportColumns';
import { ExportButton } from '../../../../common/export/ExportButton';
import { exportRows } from '../../../../common/export/exportTable';
import { useLanguage } from '../../../../common/GeneralUtils';
import { TABLE_PHONE_ID } from '../../../../table/TableComponent';
import TimeUtils from '../../../../utils/TimeUtils';
import { getDateFormat, getTimeFormat } from '../../../../common/getDateAndTimeFormat';
import { useAuth } from '../../../../session/InternalAuthProvider';
import { useCalendar } from '../../../CalendarContext/CalendarContext';
import { WaitingListUpdateDialog } from '../../../../waitingList/WaitingListUpdateDialog';
import EditOrderDialog from '../../../dialogs/EditOrderDialog/EditOrderDialog';
import OrderBoxDialog from '../../OrderBoxDialog';
import SendEmailDialog from '../../../dialogs/SendEmailDialog/SendEmailDialog';
import CopyToClipboardButton from '../../../dialogs/CopyToClipboardButton';
import { UseAuthPermissions } from '../../../../session/UseAuthPermissions';
import { mb } from '../../../../common/theme/mbTokens';
import { useEventPassHolders } from '../../../swr/useEventPassHolders';
import { eventSupportsPassHolds } from '../passHoldsVisibility';

export const waitingListFetcher =
  (eventId: string) =>
  async ({ search, page, perPage }: { search?: string; page: number; perPage: number }) =>
    WaitingListService.getWaitingList(eventId, page, perPage, search);

type TablesTabsProps = {
  event: EnrichedEventData;
  orderPage: any;
  setOrder: (order: OrderData) => void;
  setEditOrderIsOpen: (isOpen: boolean) => void;
  setCheckInDialogIsOpen: (isOpen: boolean) => void;
  isInEventMode: boolean;
  activeTab: ParticipantsTableTabValue;
  setActiveTab: (tab: ParticipantsTableTabValue) => void;
  waitingListUpdateDialogIsOpen: boolean;
  setWaitingListUpdateDialogIsOpen: (isOpen: boolean) => void;
};
const StyledWaitingListNotEmpty = styled.div`
  width: 10px;
  height: 10px;
  border-radius: 2px;
`;

export enum ParticipantsTableTabValue {
  ORDERS = '0',
  WAITING_LIST = '1',
  PASS_HOLDS = '2',
}

export const TablesTabs = ({
  event,
  orderPage,
  setOrder,
  setEditOrderIsOpen,
  setCheckInDialogIsOpen,
  isInEventMode,
  activeTab,
  setActiveTab,
  waitingListUpdateDialogIsOpen,
  setWaitingListUpdateDialogIsOpen,
}: TablesTabsProps) => {
  const theme = useTheme();
  const { users, editOrderDrawerState, closeEditOrderDrawer, openEditOrderDrawer } = useCalendar();
  const { readOnly } = UseAuthPermissions();

  const [orderOpen, setOrderOpen] = useState(false);
  const [orderId, setOrderId] = useState<string | undefined>(undefined);
  const [selectedWaitingListItem, setSelectedWaitingListItem] = useState<WaitingListItemData | undefined>();
  const [showSendEmailDialog, setShowSendEmailDialog] = useState(false);
  const [sendEmailItem, setSendEmailItem] = useState<WaitingListItemData | undefined>();
  const { authState } = useAuth();
  const account = authState.account as unknown as AccountData;
  const { shortDateFormat } = getDateFormat(account);
  const timeFormat = getTimeFormat(account);

  const { t } = useTranslation();
  const language = useLanguage();
  const {
    holders: passHolders,
    weeklyHoldersCount,
    slotCapacity,
    isLoading: isLoadingPassHolders,
  } = useEventPassHolders({
    eventId: event.id,
    enabled: eventSupportsPassHolds(account, event.order_page_id),
  });
  const showPassHolds = passHolders.length > 0 || weeklyHoldersCount > 0;
  const signedOffHoldersCount = passHolders.filter((holder) => holder.canceled_booking).length;
  useEffect(() => {
    if (!showPassHolds && activeTab === ParticipantsTableTabValue.PASS_HOLDS) {
      setActiveTab(ParticipantsTableTabValue.ORDERS);
    }
  }, [showPassHolds, activeTab, setActiveTab]);
  const handleChange = (_: any, newValue: ParticipantsTableTabValue) => {
    setActiveTab(newValue);
  };
  const tableDetails: CellProps<WaitingListItemData>[] = [
    {
      id: 'edit',
      alignment: 'left',
      width: 30,
      render: (waitingListItem: WaitingListItemData) => (
        <Tooltip title={t('edit')}>
          <span>
            <IconButton
              className="action-icon"
              onClick={() => {
                setSelectedWaitingListItem(waitingListItem);
                setWaitingListUpdateDialogIsOpen(true);
              }}>
              <Edit />
            </IconButton>
          </span>
        </Tooltip>
      ),
    },
    {
      id: 'name',
      width: 140,
      alignment: 'inherit',
      label: t('waitingList.name'),
      render: (waitingListItem: WaitingListItemData) => `${waitingListItem.firstname} ${waitingListItem.lastname}`,
    },
    {
      id: 'persons',
      width: 30,
      alignment: 'center',
      label: t('waitingList.persons'),
      render: (waitingListItem: WaitingListItemData) => waitingListItem.persons,
    },
    {
      id: TABLE_PHONE_ID,
      alignment: 'center',
      label: t('waitingList.phone'),
      width: 140,
    },
    {
      id: 'email',
      width: 200,
      alignment: 'center',
      label: t('waitingList.email'),
      render: (waitingListItem: WaitingListItemData) => (
        <Grid container gap={1} alignItems="center" flexWrap="nowrap">
          <CopyToClipboardButton text={waitingListItem.email} width="18px" color={theme.palette.text.secondary} />
          <Typography noWrap style={{ textTransform: 'none' }}>
            {waitingListItem.email}
          </Typography>
        </Grid>
      ),
    },
    {
      id: 'createdat',
      width: 100,
      alignment: 'center',
      label: t('waitinglist.createdat'),
      render: (waitingListItem: WaitingListItemData) => (
        <Typography>
          {TimeUtils.getLocalTime(waitingListItem.created_at, account.timezone).format(
            `${shortDateFormat} ${timeFormat}`,
          )}
        </Typography>
      ),
    },
    {
      id: 'status',
      alignment: 'center',
      label: t('waitinglist.statuss'),
      render: (waitingListItem: WaitingListItemData) => (
        <Typography>{t(`waitinglist.status.${waitingListItem.status?.toLowerCase()}`)}</Typography>
      ),
    },
    {
      id: 'comment',
      alignment: 'center',
      label: t('waitinglist.comment'),
      render: (waitingListItem: WaitingListItemData) => {
        const comment = waitingListItem.comment || '';
        return (
          <Tooltip title={comment.length > 150 ? comment : ''} arrow>
            <Typography
              sx={{
                maxWidth: '250px',
                whiteSpace: 'normal',
                wordBreak: 'break-word',
              }}>
              {comment.length > 150 ? `${comment.slice(0, 150)}...` : comment}
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      id: 'lastEditedBy',
      alignment: 'center',
      label: t('waitinglist.lastEditedBy'),
      render: (waitingListItem: WaitingListItemData) => {
        const user = users.find((user) => user.user_id === waitingListItem.last_updated_by_user_id);
        return <Typography noWrap>{user ? `${user.first_name} ${user.last_name}` : ''}</Typography>;
      },
    },
    {
      id: 'orderlink',
      alignment: 'left',
      width: 30,
      noPadding: true,
      label: t('vouchers.orderlink'),
      render: (waitingListItem: WaitingListItemData) =>
        waitingListItem.order_id ? (
          <Tooltip title={t('info')} arrow>
            <span>
              <IconButton
                onClick={() => {
                  setOrderOpen(true);
                  setOrderId(waitingListItem.order_id);
                }}>
                <ConfirmationNumber />
              </IconButton>
            </span>
          </Tooltip>
        ) : (
          ''
        ),
    },
    {
      id: 'redeem',
      alignment: 'left',
      width: 30,
      noPadding: true,
      render: (waitingListItem: WaitingListItemData) => (
        <Tooltip
          title={waitingListItem.order_id ? t('waitinglist.redeemdisabled') : t('waitinglist.convertToOrder')}
          arrow>
          <span>
            <IconButton
              onClick={() =>
                openEditOrderDrawer({
                  orderPageId: waitingListItem.order_page_id,
                  waitingListItem,
                })
              }
              disabled={!!waitingListItem.order_id}>
              <Redeem />
            </IconButton>
          </span>
        </Tooltip>
      ),
    },
    {
      id: 'sendEmail',
      alignment: 'left',
      width: 30,
      noPadding: true,
      render: (waitingListItem: WaitingListItemData) => (
        <Tooltip title={`${t('send')} ${waitingListItem.email}`} arrow>
          <span>
            <IconButton onClick={() => setSendEmailItem(waitingListItem)} disabled={!waitingListItem.email}>
              <Email />
            </IconButton>
          </span>
        </Tooltip>
      ),
    },
  ];
  const {
    TableComponent: WaitingListTableComponent,
    data,
    isLoadingData,
    mutate,
  } = useTable<WaitingListItemData>({
    tableDetails,
    hidePaging: true,
    defaultRowsPerPage: 1000,
    opId: 'getWaitingList',
    fetcher: waitingListFetcher(event.id),
    actionsComponent: (
      <>
        <Tooltip title={readOnly ? t('noPermissions') : t('waitinglist.sendMessage')} arrow>
          <span>
            <IconButton onClick={() => setShowSendEmailDialog(true)}>
              <Email />
            </IconButton>
          </span>
        </Tooltip>
        <ExportButton
          label={readOnly ? t('noPermissions') : t('waitinglist.downloadWaitlist')}
          onExport={(format) =>
            exportRows({
              columns: buildWaitingListExportColumns(t),
              rows: data,
              fileName: `WaitList-${event.date}`,
              format,
              language,
              sheetName: t('waitinglist.downloadWaitlist'),
            })
          }
        />
        <SendEmailDialog
          isOpen={showSendEmailDialog}
          setIsOpen={setShowSendEmailDialog}
          eventId={event.id}
          recipientType={MessageRecipientType.WAITLIST}
        />
      </>
    ),
  });
  const shouldShowNoResultsFound = !isLoadingData && !data.length;

  // fetching waiting list to show in the tab

  return (
    <TabContext value={activeTab}>
      <Grid
        container
        flexDirection="column"
        flexWrap="nowrap"
        sx={{ flex: 1, minHeight: 0, minWidth: 0, overflow: 'hidden' }}>
        {!isInEventMode && (
          <Tabs value={activeTab} onChange={handleChange} sx={{ flexShrink: 0 }}>
            <Tab
              label={
                <Grid container flexDirection="column">
                  {t('ordersTabs.orders')}
                </Grid>
              }
              value={ParticipantsTableTabValue.ORDERS}
            />
            {showPassHolds && (
              <Tab
                label={
                  <Grid container gap={0.5}>
                    {signedOffHoldersCount > 0 && (
                      <Grid>
                        <StyledWaitingListNotEmpty style={{ backgroundColor: mb.highlight }} />
                      </Grid>
                    )}
                    <Grid>{t('ordersTabs.passHolds', { count: passHolders.length })}</Grid>
                  </Grid>
                }
                value={ParticipantsTableTabValue.PASS_HOLDS}
              />
            )}
            <Tab
              label={
                <Grid container gap={0.5}>
                  {data.length > 0 && (
                    <Grid>
                      <StyledWaitingListNotEmpty style={{ backgroundColor: mb.highlight }} />
                    </Grid>
                  )}
                  <Grid>{t('ordersTabs.waitingList')}</Grid>
                </Grid>
              }
              value={ParticipantsTableTabValue.WAITING_LIST}
            />
          </Tabs>
        )}
        <TabPanel
          value={ParticipantsTableTabValue.ORDERS}
          sx={{
            p: 0,
            flex: 1,
            minHeight: 0,
            flexDirection: 'column',
            display: activeTab === ParticipantsTableTabValue.ORDERS ? 'flex' : 'none',
          }}>
          <Grid container flexDirection="column" flexWrap="nowrap" sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
            <EventParticipantsTable
              setOrder={setOrder}
              event={event}
              orderPage={orderPage}
              setEditOrderIsOpen={setEditOrderIsOpen}
              setCheckInDialogIsOpen={setCheckInDialogIsOpen}
              isInEventMode={isInEventMode}
            />
          </Grid>
        </TabPanel>
        <TabPanel
          value={ParticipantsTableTabValue.PASS_HOLDS}
          sx={{
            p: 0,
            flex: 1,
            minHeight: 0,
            flexDirection: 'column',
            display: activeTab === ParticipantsTableTabValue.PASS_HOLDS ? 'flex' : 'none',
          }}>
          <Grid
            container
            flexDirection="column"
            flexWrap="nowrap"
            mt={1}
            sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
            <EventPassHoldsTable
              holders={passHolders}
              weeklyHoldersCount={weeklyHoldersCount}
              slotCapacity={slotCapacity}
              isLoading={isLoadingPassHolders}
            />
          </Grid>
        </TabPanel>
        <TabPanel
          value={ParticipantsTableTabValue.WAITING_LIST}
          sx={{
            p: 0,
            flex: 1,
            minHeight: 0,
            flexDirection: 'column',
            display: activeTab === ParticipantsTableTabValue.WAITING_LIST ? 'flex' : 'none',
          }}>
          <Grid container flexDirection="column" flexWrap="nowrap" sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
            <Grid
              container
              flexDirection="column"
              flexWrap="nowrap"
              className="event-participant-table"
              mt={1}
              border="1px solid"
              borderColor="divider"
              borderRadius="8px"
              sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
              {shouldShowNoResultsFound ? (
                <Grid container m={4}>
                  {t('waitingList.nonfound')}
                </Grid>
              ) : (
                WaitingListTableComponent
              )}
            </Grid>
          </Grid>
          <WaitingListUpdateDialog
            open={waitingListUpdateDialogIsOpen}
            setOpen={setWaitingListUpdateDialogIsOpen}
            waitingListItem={selectedWaitingListItem}
            onUpdate={(updatedItem: WaitingListItemData) => {
              const items = data?.map((item) => (item.id === updatedItem.id ? updatedItem : item)) || [];
              void mutate(items);
            }}
          />
          <EditOrderDialog
            editOrderDialogState={editOrderDrawerState}
            closeEditOrderDialog={() => {
              void mutate();
              void closeEditOrderDrawer();
            }}
          />
          <OrderBoxDialog open={orderOpen} setOpen={setOrderOpen} orderId={orderId} />
          {sendEmailItem && (
            <SendEmailDialog
              isOpen={!!sendEmailItem}
              setIsOpen={(isOpen) => {
                if (!isOpen) {
                  setSendEmailItem(undefined);
                }
              }}
              waitingListItemId={sendEmailItem.id}
              emailAddress={sendEmailItem.email}
            />
          )}
        </TabPanel>
      </Grid>
    </TabContext>
  );
};
